import { Box } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import ContouringStepTools from '~/components/toolbar/steps/ContouringTools';
import DefaultTools from '~/components/toolbar/steps/DefaultTools';
import DoseTools from '~/components/toolbar/steps/DoseTools';
import RegistrationTools from '~/components/toolbar/steps/RegistrationTools';
import { mainViewWidthState } from '~/state/atoms/toolsAtoms';
import { showFullscreenState } from '~/state/atoms/viewerAtoms';

/**
 * Base z-index for the step toolbar overlay.
 *
 * Other overlays (e.g., DVH/Table) should be ordered relative to this layer
 * to avoid accidental stacking issues above/below the toolbar.
 */
const STEP_TOOLBAR_Z_INDEX = 10;

function useStepTools(): React.ComponentType {
    const { pathname } = useLocation();

    if (pathname.includes('/contouring') || pathname.includes('/review')) {
        return ContouringStepTools;
    }
    if (pathname.includes('/image-registration')) {
        return RegistrationTools;
    }
    if (pathname.includes('/predictive-planning')) {
        return DoseTools;
    }
    return DefaultTools;
}

export default function Toolbar() {
    const mainViewWidth = useRecoilValue(mainViewWidthState);
    const showFullscreen = useRecoilValue(showFullscreenState);
    const centerPct = showFullscreen ? 50 : mainViewWidth / 2;
    const StepTools = useStepTools();

    return (
        <Box
            data-toolbar-id="step-toolbar"
            sx={(theme) => ({
                position: 'absolute',
                top: 24,
                left: `${centerPct}%`,
                transform: 'translateX(-50%)',
                zIndex: STEP_TOOLBAR_Z_INDEX,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 0.5,
                p: 0.5,
                borderRadius: `${theme.shape.borderRadius}px`,
                ...theme.mixins.glassOverlay,
                boxShadow: '0 2px 6px rgba(0, 0, 0, 0.27)',
                border: '1px solid rgba(0, 0, 0, 0.1)',
                pointerEvents: 'auto',
            })}
        >
            <StepTools />
        </Box>
    );
}
